import type { CompanyPipelineDto, PipelineLostReasonDto, PipelineStageDto, PipelineStageUsualDto, VenuePipelineDto } from '../dto/pipeline.dto.js'
import { FUNNEL_MAIN_STEPS, FUNNEL_TARGET_LABELS, type FunnelMainStep, type FunnelStage, type FunnelTarget } from '../enums/funnel-stage.enum.js'
import { LOST_REASON_COUNT_LABELS } from '../enums/lost-reason.enum.js'
import type { PipelineComparison } from '../enums/pipeline-span.enum.js'

/**
 * The short version of a pipeline (docs/pipeline-view.spec.md, "The short
 * version"): a few plain sentences built from its numbers by fixed rules, and
 * the highlights a page links to, so the page and the email open with the
 * same answer. Nothing here guesses: each sentence restates a number the page
 * shows, and a step is only called below or above usual when its verdict says
 * so.
 */

export interface PipelineStoryInput {
  compare: PipelineComparison
  stages: readonly PipelineStageDto[]
  lostReasons: readonly PipelineLostReasonDto[]
  /** How many of the span's inquiries carry a lead source nobody has said the meaning of. */
  unmappedInquiries?: number
  /** For the company: each venue's bookings and the days compared with, to say which venues moved the total. */
  venues?: readonly PipelineVenueMove[]
}

/** One venue's bookings, and the days compared with: what moves a company's total. */
export interface PipelineVenueMove {
  name: string
  booked: number | null
  bookedBefore: number | null
}

export interface PipelineHighlight {
  /** `attention` for a step below usual, `good` above it, `neutral` otherwise. */
  tone: 'attention' | 'good' | 'neutral'
  text: string
  /** Which part of the page it is about. */
  about: 'funnel' | 'lost' | 'tracking'
}

export interface PipelineStory {
  /** At most three. Empty with nothing to say. */
  sentences: string[]
  /** At most three. */
  highlights: PipelineHighlight[]
}

/** Below this, a change in a count reads as about the same. */
const SMALL_CHANGE = 0.05
const SMALL_DIFFERENCE = 3
/** Below this many couples, a change is said in couples, never as a share. */
const SMALL_BASE = 10

/** What a couple did to reach a step, as "the couples who …" ends: "toured". */
export const PIPELINE_WHO_REACHED: Readonly<Record<FunnelStage, string>> = {
  inquiry: 'inquired',
  qualified: 'were qualified',
  corporate_reply: 'heard from the company',
  venue_reply: 'heard from the venue',
  tour_scheduled: 'booked a tour',
  open_house_rsvp: 'signed up for an open house',
  toured: 'toured',
  proposal_sent: 'had a proposal',
  date_on_hold: 'put a date on hold',
  contract_signed: 'signed',
  deposit_paid: 'paid a deposit',
  booked: 'booked',
  contract_fully_paid: 'paid in full',
  event_day: 'had their day',
}

/** A count as people read it: "1,386". */
function count(n: number): string {
  return n.toLocaleString('en-US')
}

function percent(share: number): string {
  return `${String(Math.round(share * 100))}%`
}

function against(compare: PipelineComparison): string {
  return compare === 'last_year' ? 'the same weeks last year' : 'the weeks before'
}

function stage(stages: readonly PipelineStageDto[], target: FunnelTarget): PipelineStageDto | undefined {
  return stages.find((s) => s.target === target)
}

/** Who a step's share is out of: "the couples who toured", "the couples who set up a tour or open house". */
export function couplesBefore(s: Pick<PipelineStageDto, 'before'>): string {
  if (s.before.length === 0 || (s.before.length === 1 && s.before[0] === 'inquiry')) return 'the couples who inquired'
  if (s.before.includes('tour_scheduled') && s.before.includes('open_house_rsvp')) return 'the couples who set up a tour or open house'
  const [first] = s.before
  return first === undefined ? 'the couples who inquired' : `the couples who ${PIPELINE_WHO_REACHED[first]}`
}

function inquiriesSentence(s: PipelineStageDto | undefined, compare: PipelineComparison): string | null {
  if (s?.value == null) return null
  const v = s.value
  if (v === 0) return 'No couples inquired in these weeks.'
  const p = s.previous
  const couples = `${count(v)} ${v === 1 ? 'couple' : 'couples'}`
  if (p === null) return `${couples} inquired.`
  if (p === 0) return `${couples} inquired, where none did ${against(compare)}.`
  const change = (v - p) / p
  if (Math.abs(v - p) < SMALL_DIFFERENCE || Math.abs(change) < SMALL_CHANGE) {
    return `Inquiries are about the same as ${against(compare)}: ${count(v)} against ${count(p)}.`
  }
  // A share of a handful says nothing: below ten couples, just the two counts.
  const by = p < SMALL_BASE ? '' : ` ${percent(Math.abs(change))}`
  return `Inquiries are ${change > 0 ? 'up' : 'down'}${by} on ${against(compare)}: ${count(v)} against ${count(p)}.`
}

/** What judging a step reads of it. */
type JudgedStage = Pick<PipelineStageDto, 'target' | 'before' | 'usual'>

/** A main step with its verdict. */
interface Judged {
  target: FunnelMainStep
  stage: JudgedStage
  usual: PipelineStageUsualDto
}

/** The main steps that have a verdict, in order, Inquiry aside. */
function judged(stages: readonly JudgedStage[]): Judged[] {
  return FUNNEL_MAIN_STEPS.flatMap((target) => {
    const s = stages.find((x) => x.target === target)
    return s?.usual == null || target === 'inquiry' ? [] : [{ target, stage: s, usual: s.usual }]
  })
}

/**
 * How far a step is off what's usual by now, as a share of the usual: a step
 * 20% under its usual is further off than one 5% under, whatever their sizes.
 */
function offBy(u: PipelineStageUsualDto): number {
  const share = u.part / u.whole
  if (u.byNow === 0) return share
  return Math.abs(share / u.byNow - 1)
}

/** The step with a verdict that's furthest off what's usual; undefined when no step has it. */
function furthest(steps: readonly Judged[], verdict: 'below' | 'above'): Judged | undefined {
  return steps.filter((s) => s.usual.verdict === verdict).sort((a, b) => offBy(b.usual) - offBy(a.usual))[0]
}

/** The main step furthest below what's usual: the one the short version names, and the email looks at first. Null when none is below. */
export function furthestBelowUsual<S extends JudgedStage>(stages: readonly S[]): S | null {
  const below = furthest(judged(stages), 'below')
  return below === undefined ? null : (stages.find((s) => s.target === below.target) ?? null)
}

function verdictSentence(steps: readonly Judged[], company: boolean): string | null {
  const below = furthest(steps, 'below')
  const notable = below ?? furthest(steps, 'above')
  if (notable === undefined) {
    if (steps.length === 0) return null
    return company ? 'Every step is running about as usual across the venues.' : 'Every step is running about as usual for this venue.'
  }
  const { part, whole, byNow, settledOnly } = notable.usual
  const more = below === undefined ? 'More' : 'Fewer'
  const judged = settledOnly ? ', among those old enough to judge' : ''
  return `${more} of ${couplesBefore(notable.stage)} ${PIPELINE_WHO_REACHED[notable.target]} than usual${judged}: ${percent(part / whole)} against ${percent(byNow)} by now.`
}

function bookedSentence(s: PipelineStageDto | undefined, compare: PipelineComparison): string | null {
  if (s?.value == null || s.previous === null) return null
  const diff = s.value - s.previous
  if (Math.abs(diff) < 2) return null
  const sameAge = s.sameAge ? ' at the same age' : ''
  return `Bookings are ${diff > 0 ? 'up' : 'down'} ${count(Math.abs(diff))} on ${against(compare)}: ${count(s.value)} against ${count(s.previous)}${sameAge}.`
}

/** The top reason couples give after a tour, else overall; one recorded reason, at least three couples. */
function topLoss(reasons: readonly PipelineLostReasonDto[]): { reason: PipelineLostReasonDto; afterTour: boolean } | null {
  const recorded = reasons.filter((r) => r.reason !== 'not_recorded')
  const after = [...recorded].sort((a, b) => (b.afterTour ?? 0) - (a.afterTour ?? 0))[0]
  if (after !== undefined && (after.afterTour ?? 0) >= 3) return { reason: after, afterTour: true }
  const overall = [...recorded].sort((a, b) => b.count - a.count)[0]
  return overall !== undefined && overall.count >= 3 ? { reason: overall, afterTour: false } : null
}

function lossSentence(top: ReturnType<typeof topLoss>): string | null {
  if (top === null) return null
  const label = LOST_REASON_COUNT_LABELS[top.reason.reason]
  return top.afterTour
    ? `${label} is the top reason couples give after a tour (${count(top.reason.afterTour ?? 0)}).`
    : `${label} is the most common reason couples were lost (${count(top.reason.count)}).`
}

/** "A, B and C". */
function listed(names: readonly string[]): string {
  if (names.length <= 1) return names.join('')
  return `${names.slice(0, -1).join(', ')} and ${names.at(-1) ?? ''}`
}

/** The share of a company's change in bookings a venue or two must make up to be named as its cause. */
const MOST_OF_IT = 0.7

/** The one or two venues that make up most of a company's change in bookings, when one or two do. */
function moversSentence(venues: readonly PipelineVenueMove[] | undefined, booked: PipelineStageDto | undefined): string | null {
  if (venues === undefined || booked?.value == null || booked.previous === null) return null
  const total = booked.value - booked.previous
  if (Math.abs(total) < SMALL_DIFFERENCE) return null
  const moved = venues
    .flatMap((v) => (v.booked === null || v.bookedBefore === null ? [] : [{ name: v.name, diff: v.booked - v.bookedBefore }]))
    .filter((v) => Math.sign(v.diff) === Math.sign(total))
    .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff))
  const named: string[] = []
  let sum = 0
  for (const v of moved) {
    named.push(v.name)
    sum += v.diff
    if (Math.abs(sum) >= MOST_OF_IT * Math.abs(total)) break
  }
  if (named.length === 0 || named.length > 2 || Math.abs(sum) < MOST_OF_IT * Math.abs(total)) return null
  return `${listed(named)} ${named.length === 1 ? 'accounts' : 'account'} for most of the ${total < 0 ? 'drop' : 'rise'}.`
}

export function pipelineStory(input: PipelineStoryInput): PipelineStory {
  const steps = judged(input.stages)
  const top = topLoss(input.lostReasons)
  const company = input.venues !== undefined
  const inquiries = inquiriesSentence(stage(input.stages, 'inquiry'), input.compare)
  const verdict = verdictSentence(steps, company)
  const booked = bookedSentence(stage(input.stages, 'booked'), input.compare)
  const movers = moversSentence(input.venues, stage(input.stages, 'booked'))
  const loss = lossSentence(top)
  // A company's story starts with its bookings and the venues that moved them; a venue's with its inquiries.
  const sentences = (company ? [booked, movers, inquiries, verdict, loss] : [inquiries, verdict, booked, loss])
    .filter((s): s is string => s !== null)
    .slice(0, 3)

  const highlights: PipelineHighlight[] = [
    ...steps
      .filter((s) => s.usual.verdict === 'below')
      .map((s): PipelineHighlight => ({ tone: 'attention', text: `${FUNNEL_TARGET_LABELS[s.target]}: below usual`, about: 'funnel' })),
    ...steps
      .filter((s) => s.usual.verdict === 'above')
      .map((s): PipelineHighlight => ({ tone: 'good', text: `${FUNNEL_TARGET_LABELS[s.target]}: above usual`, about: 'funnel' })),
    ...(top?.afterTour === true
      ? [{ tone: 'neutral' as const, text: `${LOST_REASON_COUNT_LABELS[top.reason.reason]} after a tour: ${count(top.reason.afterTour ?? 0)}`, about: 'lost' as const }]
      : []),
    ...((input.unmappedInquiries ?? 0) > 0
      ? [{ tone: 'neutral' as const, text: `${count(input.unmappedInquiries ?? 0)} inquiries with no known source`, about: 'tracking' as const }]
      : []),
  ].slice(0, 3)

  return { sentences, highlights }
}

/** What stands out at one venue, for a company's venues table. */
export interface PipelineVenueNumbers {
  values: readonly Pick<PipelineStageDto, 'target' | 'value' | 'previous' | 'before' | 'usual'>[]
  cost: { micros: number } | null
  compared: { costMicros: number | null } | null
}

/** A change big enough to name at one venue: a quarter or more, and at least ten couples. */
const NOTABLE_SHARE = 0.25
const NOTABLE_COUPLES = 10

/**
 * What stands out at one venue, in a few words: a main step below usual, a big
 * change in inquiries (and in what marketing cost, when that moved too), a step
 * above usual, or bookings ahead or behind. Null when nothing does.
 */
export function venueStandout(venue: PipelineVenueNumbers, compare: PipelineComparison): string | null {
  const steps = judged(venue.values)
  const step = (verdict: 'below' | 'above'): string | null => {
    const found = furthest(steps, verdict)
    if (found === undefined) return null
    const { part, whole, byNow } = found.usual
    return `${verdict === 'below' ? 'Fewer' : 'More'} of ${couplesBefore(found.stage)} ${PIPELINE_WHO_REACHED[found.target]}: ${percent(part / whole)}, against ${percent(byNow)} usually by now`
  }
  const below = step('below')
  if (below !== null) return below

  const inquiry = venue.values.find((v) => v.target === 'inquiry')
  if (inquiry?.value != null && inquiry.previous !== null && inquiry.previous > 0) {
    const change = (inquiry.value - inquiry.previous) / inquiry.previous
    if (Math.abs(change) >= NOTABLE_SHARE && Math.abs(inquiry.value - inquiry.previous) >= NOTABLE_COUPLES) {
      if (inquiry.previous < SMALL_BASE) return `Inquiries ${change > 0 ? 'up' : 'down'} from ${count(inquiry.previous)} to ${count(inquiry.value)} on ${against(compare)}`
      const before = venue.compared?.costMicros ?? null
      const costChange = venue.cost === null || before === null || before === 0 ? null : (venue.cost.micros - before) / before
      const cost =
        costChange !== null && Math.abs(costChange) >= NOTABLE_SHARE && Math.sign(costChange) === Math.sign(change)
          ? `, with marketing cost ${costChange > 0 ? 'up' : 'down'} ${percent(Math.abs(costChange))}`
          : ''
      return `Inquiries ${change > 0 ? 'up' : 'down'} ${percent(Math.abs(change))} on ${against(compare)}${cost}`
    }
  }

  const above = step('above')
  if (above !== null) return above

  const booked = venue.values.find((v) => v.target === 'booked')
  if (booked?.value != null && booked.previous !== null && Math.abs(booked.value - booked.previous) >= 2) {
    const diff = booked.value - booked.previous
    return diff > 0 ? `${count(diff)} more bookings than ${against(compare)}` : `${count(-diff)} fewer bookings than ${against(compare)}`
  }
  return null
}

/** A venue's short version, from its pipeline: what its page, its email and an assistant all open with. */
export function venueStoryInput(d: Pick<VenuePipelineDto, 'compare' | 'stages' | 'lostReasons' | 'tracking'>): PipelineStoryInput {
  return { compare: d.compare, stages: d.stages, lostReasons: d.lostReasons, unmappedInquiries: d.tracking?.unmappedInquiries ?? 0 }
}

/** The company's short version, from its pipeline: its venues' bookings name the ones that moved the total. */
export function companyStoryInput(d: Pick<CompanyPipelineDto, 'compare' | 'stages' | 'lostReasons' | 'rows'>): PipelineStoryInput {
  return {
    compare: d.compare,
    stages: d.stages,
    lostReasons: d.lostReasons,
    unmappedInquiries: d.rows.reduce((a, r) => a + (r.tracking?.unmappedInquiries ?? 0), 0),
    venues: d.rows.map((r) => {
      const booked = r.values.find((v) => v.target === 'booked')
      return { name: r.venueName, booked: booked?.value ?? null, bookedBefore: booked?.previous ?? null }
    }),
  }
}
